import React, { useEffect, useState } from "react";
import LavorisService from "../../services/LavorisService";
import { TableActions } from "../../components/others/TableActions";
import { useModal } from "../../hooks";
import { DetailTurnWithDate } from "../others/detailsTurn";
import moment from "moment";
import { filterMoldeExamPractice } from "../../utils/filterMoldeExamPractice";
import { useSelector } from "react-redux";
import { RootState } from "../../store/combineReducer";
import { useAlert } from "../../hooks/useAlert";
import { employeeType } from "../../utils/employeeType";
import { appointmentStatus } from "../../utils/appointmentStatus";
import exelReports from "../Results/exelReports";
import { filterCreator } from "../../utils/filterCreator";
import GetFollowUpMessages from "../others/GetFollowUps/getFollowUpMessage";
import InfiniteTable from "../../components/others/InfiniteTableComponent/IntiniteTableComponent";
import { formatDateTimeForTable } from "../../utils/formatDate";
import "./history_turn.scss";

const VIEW_APPOINTMENTS_MODAL = "viewAppointmentsModal";
const VIEW_APPOINTMENT_FOLLOW_UP = 'viewAppointmentFollowUpModal';

const HistoryTurn = () => {
    const today = new Date();
    const formattedToday = today.toISOString().split("T")[0]; // YYYY-MM-DD
    const maxDate = "9999-12-31";
    const [from, setFrom] = useState<string>(formattedToday);
    const [to, setTo] = useState<string>(maxDate);
    const [fromFormatted, setFromFormatted] = useState<string>(moment().format('DD/MM/YYYY'));
    const [toFormatted, setToFormatted] = useState<string>(moment().add(365, 'days').format('DD/MM/YYYY'));
    const [hasMore, setHasMore] = useState(true);
    const [searchQuery, setSearchQuery] = useState<string | null>(null);
    const hasSearchQuery = searchQuery && searchQuery.length > 0;
    const LavorisInstance = LavorisService.getInstance();
    const currentCompany = useSelector((state: RootState) => state.user.currentCompany || state.user.companies[0].id);
    const { closeModal, openModal, isActive } = useModal();
    const { showAlert } = useAlert();
    const [items, setItems] = useState({
        itemsPerPage: 20,
        page: 1,
    });
    const [data, setData] = useState<any[]>([]);
    const [loader, setLoader] = useState(false);
    const [excelLoading, setExcelLoading] = useState(false);

    const status = ['Pendiente de confirmación', 'Confirmado', 'Presente en Lavoris', 'Pendiente de completar', 'Aptitud parcialmente completada', 'Finalizado', 'Retirado', 'Eliminado', 'Ausente'];

    const controller = new AbortController();
    const signal = controller.signal;

    // GET ALL APPOINTMENTS FOR TABLE
    const getAllAppointmentsCall = async () => {
        setLoader(true);
        try {
            const app = await LavorisInstance.TurnServices.getAppointmentsWithSearchQueryByCompaniesId(
                currentCompany,
                items.itemsPerPage,
                items.page,
                fromFormatted,
                toFormatted,
                signal,
                hasSearchQuery ? searchQuery : "",
                status
            );

            if (app.items.length === 0) {
                setHasMore(false);
                return;
            }

            const existingIds = new Set(data?.map(item => item.id));
            const uniqueAppointments = app?.items?.filter(appointment => !existingIds.has(appointment.id));

            setData(prevData => [...prevData, ...uniqueAppointments]);
            setItems(prev => ({ ...prev, page: prev.page + 1 }));
        } catch (e) {
            console.log(e);
            setHasMore(false);
        } finally {
            setLoader(false);
        }
    };

    const refreshAppointments = async () => {
        setLoader(true);
        try {
            setHasMore(true);
            setData([]);
            setItems({ itemsPerPage: 20, page: 1 });

            const app = await LavorisInstance.TurnServices.getAppointmentsWithSearchQueryByCompaniesId(
                currentCompany,
                items.itemsPerPage,
                1,
                fromFormatted,
                toFormatted,
                signal,
                hasSearchQuery ? searchQuery || "" : "",
                status
            );

            if (app.items.length === 0) {
                showAlert("No se encontraron registros");
                return;
            }

            setData(app.items);
            setItems(prev => ({ ...prev, page: 2 }));
            setHasMore(true);
        } catch (error) {
            console.log(error);
            setHasMore(false);
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        getAllAppointmentsCall();
    }, []);

    useEffect(() => {
        return () => {
            controller.abort();
        };
    }, []);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const querySearch = e.target.value;
        setSearchQuery(querySearch);
    };

    const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const dateValue = e.target.value;
        setFrom(dateValue);
        setFromFormatted(moment(dateValue).format('DD/MM/YYYY'));
    };

    const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const dateValue = e.target.value;
        setTo(dateValue);
        setToFormatted(moment(dateValue).format('DD/MM/YYYY'));
    };

    const onSearch = async () => {
        await refreshAppointments();
    };

    const handleExcelDownload = async () => {
        setExcelLoading(true);
        try {
            await exelReports(data, false);
        } catch (error) {
            console.log(error);
            showAlert("Error al generar el reporte de Excel");
        } finally {
            setExcelLoading(false);
        }
    };

    const returnRetiro = (rowData: any) => {
        if (!rowData?.actions || rowData?.actions?.length === 0) return '---';
        const retirado = rowData?.actions?.filter((action: any) => action?.action === 'RETIRADO');
        if (retirado.length === 0) return '---';
        return moment(retirado[0]?.date).format('DD/MM/YY HH:mm');
    };

    const giaSolicitud = (rowData: any) => {
        if (!rowData) return '---';
        const hasGia = rowData?.gia;
        return (
            <span className={`gia-badge ${hasGia ? 'gia-yes' : 'gia-no'}`}>
                {hasGia ? 'Sí' : 'No'}
            </span>
        );
    };

    // Ordenar datos
    const sortedData = [...data].sort((a, b) => {
        const parseDate = (dateStr: string | null | undefined): number => {
            if (!dateStr) return new Date(a.createdDate).getTime();
            return new Date(dateStr.split('/').reverse().join('-')).getTime();
        };

        const dateA = parseDate(a.date);
        const dateB = parseDate(b.date);

        if (dateA === dateB) {
            const timeA = a.startTime ? a.startTime.split(':').map(Number) : [0, 0];
            const timeB = b.startTime ? b.startTime.split(':').map(Number) : [0, 0];
            return timeB[0] - timeA[0] || timeB[1] - timeA[1];
        }

        return dateB - dateA;
    });

    // Configuración de columnas para la tabla
    const appointmentColumns = [
        {
            key: 'patient_name',
            title: 'Apellido y Nombre',
            className: 'col-patient-name-turn',
            expandable: false,
            render: (value: any, row: any) => row?.patient?.name?.toUpperCase() || "---"
        },
        {
            key: 'patient_dni',
            title: 'DNI',
            className: 'col-dni-turn',
            responsive: 'tablet' as const,
            expandable: false,
            render: (value: any, row: any) => row?.patient?.DNI || "---"
        },
        {
            key: 'gia',
            title: 'AI',
            className: 'col-gia',
            responsive: 'tablet' as const,
            expandable: false,
            render: (value: any, row: any) => giaSolicitud(row)
        },
        {
            key: 'type',
            title: 'Tipo',
            className: 'col-type',
            responsive: 'desktop' as const,
            expandable: false,
            render: (value: any, row: any) => employeeType(row?.patient?.type) || "---"
        },
        {
            key: 'appointment_date',
            title: 'Fecha y hora del turno',
            className: 'col-appointment-date-turn',
            expandable: false,
            render: (value: any, row: any) => formatDateTimeForTable(row?.date, row?.startTime)
        },
        {
            key: 'admission_time',
            title: 'Fecha y hora de admisión',
            className: 'col-admission-time-turn',
            responsive: 'desktop' as const,
            expandable: false,
            render: (value: any, row: any) => row?.admissionTime || '---'
        },
        {
            key: 'retirement_time',
            title: 'Fecha y hora de retiro',
            className: 'col-retirement-time-turn',
            responsive: 'desktop' as const,
            expandable: false,
            render: (value: any, row: any) => returnRetiro(row)
        },
        {
            key: 'creator',
            title: 'Solicitante',
            className: 'col-email',
            expandable: false,
            render: (value: any, row: any) => (
                <div>
                    {filterCreator(row)}
                </div>
            )
        },
        {
            key: 'status',
            title: 'Estado',
            className: 'col-status',
            expandable: false,
            render: (value: any, row: any) => appointmentStatus(row)
        },
        {
            key: 'description',
            title: 'Descripción',
            className: 'col-description',
            expandable: false,
            render: (value: any, row: any) => filterMoldeExamPractice(row) || "---"
        },
        {
            key: 'actions',
            title: 'Acciones',
            className: 'col-actions',
            expandable: false,
            fixed: false,
            render: (value: any, row: any) => (
                <div className="flex flex-wrap gap-2 justify-center">
                    <TableActions
                        type="View"
                        onClick={() => openModal(VIEW_APPOINTMENTS_MODAL, row.id)}
                    />
                    <TableActions
                        type='Messages'
                        color={row.appointment_follow_up.length > 0 ? 'orange' : 'blue'}
                        title={row.appointment_follow_up.length > 0 ? 'Ver mensajes de gestión' : 'No hay mensajes de gestión'}
                        onClick={() => openModal(VIEW_APPOINTMENT_FOLLOW_UP, row.id)}
                    />
                    <TableActions
                        type="Time"
                        title={row?.status === 'Ausente' ? 'No se presentó' : (row?.tardiness || '')}
                        color={row?.status === 'Ausente' || row?.status === 'Eliminado' || row?.status === 'Cancelado'
                            ? 'blue'
                            : (row?.is_late ? 'red' : 'green')
                        }
                        onClick={() => { }}
                    />
                </div>
            )
        }
    ];

    return (
        <div className="history-turns-page">

            <div className="custom-header mt-4 pt-2">
                <div className="header-controls">
                    {/* Search Section */}
                    <div className="search-section">
                        <div className="search-input-group">
                            <div className="search-input-label-top">
                                <label htmlFor="search-dni">Buscar por DNI o Apellido</label>
                                <input
                                    title="Ingresar dni o apellido"
                                    type="search"
                                    placeholder="Ingresa un DNI o Apellido"
                                    className="search-input"
                                    onChange={handleSearchChange}
                                />
                            </div>
                            <div className="search-input-label-top">
                                <label htmlFor="search-date-from">Desde</label>
                                <input
                                    title="Desde"
                                    type="date"
                                    value={from ?? ""}
                                    placeholder="Desde"
                                    className="search-input"
                                    onChange={handleFromDateChange}
                                />
                            </div>
                            <div className="search-input-label-top">
                                <label htmlFor="search-date-to">Hasta</label>
                                <input
                                    title="Hasta"
                                    type="date"
                                    value={to ?? ""}
                                    placeholder="Hasta"
                                    className="search-input"
                                    onChange={handleToDateChange}
                                />
                            </div>
                            <button
                                type="button"
                                className="search-btn"
                                onClick={() => {
                                    onSearch()
                                }}
                                title="Buscar"
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path
                                        d="M7.333 12.667A5.333 5.333 0 1 0 7.333 2a5.333 5.333 0 0 0 0 10.667ZM14 14l-2.9-2.9"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Actions Section */}
                    <div className="actions-section">
                        <div className="results-count">
                            <span className="count-number">{data.length}</span>
                            <span className="count-label">turnos</span>
                        </div>
                        {data.length >= 1 && (
                            <button
                                className="download-btn"
                                onClick={() => exelReports(data, false)}
                                title="Descargar Reporte (hacer clic y esperar)"
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path
                                        d="M14 10v2.667A1.333 1.333 0 0112.667 14H3.333A1.333 1.333 0 012 12.667V10M8 10.667L8 2M8 10.667l-2.667-2.666M8 10.667l2.667-2.666"
                                        stroke="currentColor"
                                        strokeWidth="1.3"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <span className="btn-text">Excel</span>
                            </button>
                        )}
                    </div>

                </div>
            </div>

            {/* Table Section */}
            <InfiniteTable
                data={sortedData}
                columns={appointmentColumns}
                loading={loader}
                hasMore={hasMore}
                onLoadMore={getAllAppointmentsCall}
                height={470}
                emptyMessage="Momentáneamente no hay turnos cargados para mostrar"
                loadingMessage={data.length === 0 ? "Cargando turnos..." : "Buscando más turnos..."}
                striped
                hover
                bordered
            />

            {/* Modals */}
            {isActive(VIEW_APPOINTMENTS_MODAL) && (
                <DetailTurnWithDate
                    show
                    modalTitle={"Detalle de turno"}
                    urlTittle={VIEW_APPOINTMENTS_MODAL}
                    closeModal={() => closeModal(VIEW_APPOINTMENTS_MODAL)}
                />
            )}

            {isActive(VIEW_APPOINTMENT_FOLLOW_UP) && (
                <GetFollowUpMessages
                    show
                    urlTittle={VIEW_APPOINTMENT_FOLLOW_UP}
                    onClose={() => closeModal(VIEW_APPOINTMENT_FOLLOW_UP)}
                    modalTitle="VER MENSAJES DE GESTIÓN"
                />
            )}
        </div>
    );
};

export default HistoryTurn;